import React from 'react';
import { observer } from 'mobx-react';
import { Table, Tooltip } from 'antd';
import { CloseCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { apiConfig } from '../../../REST';
import { IInvoice, IInvoicePdfFileStatus } from '../../../interfaces';
import { useInvoicesList, useUiIsLoading, useUiSearch } from '../../../hooks';
import { dateText2Text, lightText } from '../../../instruments';
import { tF, tP } from '../../../translate';
import { Catcher, FileTypeIcons, LinkComponent, TableItemToolbar, columnToolbar } from '../../../Components';
import '../../../customStyle/table.less';
import StylesIcon from '../../IconComponents/m_styles.less';
import StylesLink from '../../LinkComponent/m_styles.less';
import Styles from '../../styles/m_viewStyles.less';

const getStatusContent = (i: IInvoice, status?: IInvoicePdfFileStatus) => {
    switch (status) {
        case 'processing':
            return (
                <div style={{ display: 'flex', alignItems: 'center', color: '#1890ff' }}>
                    <LoadingOutlined style={{ fontSize: '30px', marginRight: '10px' }} />
                    <span>Processing...</span>
                </div>
            );
        case 'error':
            return (
                <div style={{ display: 'flex', alignItems: 'center', color: '#ff4d4f' }}>
                    <CloseCircleOutlined style={{ fontSize: '30px', marginRight: '10px' }} />
                    <span>Failed to generate PDF</span>
                </div>
            );
        case 'generated':
            return (
                <Tooltip title="Download PDF">
                    <a href={`${apiConfig.apiUrl()}${i.fileUrl}`} className={StylesLink.linkItem} title="Download PDF">
                        <FileTypeIcons fileType={'pdf'} className={StylesIcon.icon} />
                    </a>
                </Tooltip>
            );
        default:
            return null;
    }
};

export const InvoicesTableList: React.FC = observer(() => {
    const isLoading = useUiIsLoading();
    const list = useInvoicesList();
    const search = useUiSearch();

    const tableColumns = [
        {
            title: tF('ID'),
            dataIndex: 'idLink',
        },
        {
            title: tF('Code'),
            dataIndex: 'codeLink',
        },
        {
            title: tF('Iteration'),
            dataIndex: 'iteration',
        },
        {
            title: tF('Seller'),
            dataIndex: 'sellerLink',
        },
        {
            title: tF('Buyer'),
            dataIndex: 'buyerLink',
        },

        {
            title: tF('Date'),
            dataIndex: 'date',
        },

        {
            title: tF('Sent at'),
            dataIndex: 'sentAt',
        },
        {
            title: tF('Invoice'),
            dataIndex: 'invoiceLink',
        },

        columnToolbar,
    ];

    const tableData = list.map((i: IInvoice) => {
        return {
            key: i.id,
            ...i,
            idLink: <LinkComponent currentListComponent={'InvoicesList'} id={i.id.toString()} isLight />,
            codeLink: (
                <LinkComponent currentListComponent={'InvoicesList'} id={i.id.toString()} isLight name={i.code} />
            ),
            iteration: lightText(i.invoiceIteration, search),
            sellerLink: i.sellerId ? (
                <LinkComponent
                    currentListComponent={'BillingCompaniesList'}
                    id={i.sellerId.toString()}
                    isLight
                    name={i.seller}
                />
            ) : (
                lightText(i.seller, search)
            ),
            buyerLink: i.buyerId ? (
                <LinkComponent
                    currentListComponent={'BillingCompaniesList'}
                    id={i.buyerId.toString()}
                    isLight
                    name={i.buyer}
                />
            ) : (
                lightText(i.buyer, search)
            ),
            date: dateText2Text(i.date),
            sentAt: dateText2Text(i.sentAt),
            toolbar: (
                <TableItemToolbar
                    id={i.id.toString()}
                    itemName={`${tP('Invoice')}`}
                    currentListComponent={'InvoicesList'}
                />
            ),
            invoiceLink: i.fileUrl ? getStatusContent(i, i.pdfFileStatus) : null,
        };
    });

    return (
        <Catcher>
            <div className={Styles.tablePlace}>
                <Table
                    className={'table-theme'}
                    columns={tableColumns}
                    dataSource={tableData}
                    loading={isLoading}
                    pagination={false}
                    size={'middle'}
                />
            </div>
        </Catcher>
    );
});
